import { client } from '@platform/http/api-client';

const foldersApi = {
  // ── Work Item Layout Editor (layout only — see the handler's header) ──
  // Returns: { projectId, issueTypeId, projectName, workTypeName,
  // screen{id,name,sharedWith[],projectCount}, tabs[], contextFields[],
  // projectsWithWorkType[], layoutSource }. There is no field palette: adding
  // or removing fields and any tab change are screen edits, done in
  // Settings → Screens.
  async getWorkItemLayoutEditor(projectId, issueTypeId) {
    const response = await client.get(`/api/work-item-layout-editor/${encodeURIComponent(projectId)}/${encodeURIComponent(issueTypeId)}`);
    return response.data;
  },

  // Save layout edits. Body: { screenId, contextFields } — layout only; the
  // server derives the main column from the live screen and never writes
  // screen tables.
  async saveWorkItemLayoutEditor(projectId, issueTypeId, payload) {
    const response = await client.post(
      `/api/work-item-layout-editor/${encodeURIComponent(projectId)}/${encodeURIComponent(issueTypeId)}`,
      payload,
    );
    return response.data;
  },

  // Apply this work type's layout to the SAME work type in other PROJECTS —
  // Jira's "apply to other projects". Body: { targetProjectIds: [string] }.
  // (Was a copy across work types within one project; that isn't the thing
  // anyone wants to roll out.)
  async copyWorkItemLayoutEditor(projectId, issueTypeId, targetProjectIds) {
    const response = await client.post(
      `/api/work-item-layout-editor/${encodeURIComponent(projectId)}/${encodeURIComponent(issueTypeId)}/copy`,
      { targetProjectIds },
    );
    return response.data;
  },

  // Time in Status column: { [issueKey]: <entered-current-status ISO> } for the
  // whole site (or the given keys). The column renders "now − started".
  async getTimeInStatus(keys) {
    const response = await client.post('/api/folders/time-in-status', (keys && keys.length) ? { keys } : {});
    return response.data;
  },

  // Structures
  async getStructures() {
    const response = await client.get('/api/structures');
    return response.data;
  },
  async createStructure(data) {
    const response = await client.post('/api/structures', data);
    return response.data;
  },
  async updateStructure(id, data) {
    const response = await client.put(`/api/structures/${id}`, data);
    return response.data;
  },
  async switchInsight(id, insightId) {
    const response = await client.put(`/api/structures/${id}/insight`, { insightId });
    return response.data;
  },
  async deleteStructure(id) {
    const response = await client.delete(`/api/structures/${id}`);
    return response.data;
  },
  async copyStructure(id, name) {
    const response = await client.post(`/api/structures/${id}/copy`, { name });
    return response.data;
  },

  // User Insight Preferences
  async getUserInsightPrefs() {
    const response = await client.get('/api/user-insight-prefs');
    return response.data;
  },
  async setUserInsightPref(structureId, insightId) {
    const response = await client.put(`/api/user-insight-prefs/${structureId}`, { insightId });
    return response.data;
  },

  // Containers
  async getContainers(structureId = null) {
    const params = structureId ? { structureId } : {};
    const response = await client.get('/api/containers', { params });
    return response.data;
  },
  async createContainer(data) {
    const response = await client.post('/api/containers', data);
    return response.data;
  },
  async updateContainer(id, data) {
    const response = await client.put(`/api/containers/${id}`, data);
    return response.data;
  },
  async deleteContainer(id) {
    const response = await client.delete(`/api/containers/${id}`);
    return response.data;
  },
  async reorderContainers(containerIds) {
    console.log('API reorderContainers called with:', containerIds);
    try {
      const response = await client.put('/api/containers/reorder', { containerIds });
      return response.data;
    } catch (error) {
      console.error('Reorder API error details:', error.response?.data);
      throw error;
    }
  },
  // Columns
  async getColumns(structureId = null) {
    const params = structureId ? { structureId } : {};
    const response = await client.get('/api/columns', { params });
    return response.data;
  },
  async createColumn(data) {
    const response = await client.post('/api/columns', data);
    return response.data;
  },
  async updateColumn(id, data) {
    const response = await client.put(`/api/columns/${id}`, data);
    return response.data;
  },
  async deleteColumn(id) {
    const response = await client.delete(`/api/columns/${id}`);
    return response.data;
  },
  async reorderColumns(columnIds) {
    const response = await client.put('/api/columns/reorder', { columnIds });
    return response.data;
  },

  // Insights
  async getInsights(structureId = null) {
    const params = structureId ? { structureId } : {};
    const response = await client.get('/api/insights', { params });
    return response.data;
  },
  async createInsight(data) {
    const response = await client.post('/api/insights', data);
    return response.data;
  },
  async updateInsight(id, data) {
    const response = await client.put(`/api/insights/${id}`, data);
    return response.data;
  },
  async deleteInsight(id) {
    const response = await client.delete(`/api/insights/${id}`);
    return response.data;
  },
  async reorderInsights(insightIds) {
    const response = await client.put('/api/insights/reorder', { insightIds });
    return response.data;
  },

  // User Columns (personal per-user columns)
  async getUserColumns(structureId) {
    const response = await client.get('/api/user-columns', { params: { structureId } });
    return response.data;
  },
  async createUserColumn(data) {
    const response = await client.post('/api/user-columns', data);
    return response.data;
  },
  async updateUserColumn(id, data) {
    const response = await client.put(`/api/user-columns/${id}`, data);
    return response.data;
  },
  async deleteUserColumn(id) {
    const response = await client.delete(`/api/user-columns/${id}`);
    return response.data;
  },
  async reorderUserColumns(columnIds) {
    const response = await client.put('/api/user-columns/reorder', { columnIds });
    return response.data;
  },
};

export default foldersApi;
